import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Heart, Star, Plus, X, Camera, Calendar,
  MessageCircle, Flame, Flower2, Send, Lock, Globe, 
  Share2, ExternalLink, Sparkles, Cloud, Sun, Moon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Virtual Gifts
const VIRTUAL_GIFTS = [
  { id: 'candle', name: 'Light a Candle', icon: '🕯️', emoji: '🕯️' },
  { id: 'flower', name: 'Send Flowers', icon: '💐', emoji: '💐' },
  { id: 'heart', name: 'Send Love', icon: '❤️', emoji: '❤️' },
  { id: 'rainbow', name: 'Rainbow', icon: '🌈', emoji: '🌈' },
  { id: 'star', name: 'Bright Star', icon: '⭐', emoji: '⭐' },
  { id: 'paw', name: 'Paw Print', icon: '🐾', emoji: '🐾' },
];

// Donation Partners (placeholder)
const DONATION_PARTNERS = [
  { id: 'cancer', name: 'Pet Cancer Research', url: 'https://example.com' },
  { id: 'shelter', name: 'Local Animal Shelters', url: 'https://example.com' },
  { id: 'rescue', name: 'Dog Rescue Organizations', url: 'https://example.com' },
];

// Floating candle animation component
const FloatingCandle = ({ delay = 0 }) => (
  <motion.div
    className="absolute text-2xl"
    initial={{ opacity: 0, y: 100, x: Math.random() * 100 - 50 }}
    animate={{ 
      opacity: [0, 1, 1, 0],
      y: [100, -100],
      x: [Math.random() * 100 - 50, Math.random() * 100 - 50]
    }}
    transition={{ 
      duration: 4,
      delay,
      repeat: Infinity,
      repeatDelay: Math.random() * 2
    }}
  >
    🕯️
  </motion.div>
);

// Memorial Card Component
const MemorialCard = ({ memorial, onClick }) => {
  const daysSince = Math.floor((new Date() - new Date(memorial.passed_date)) / (1000 * 60 * 60 * 24));
  const isAnniversary = new Date(memorial.passed_date).getMonth() === new Date().getMonth() &&
                        new Date(memorial.passed_date).getDate() === new Date().getDate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      onClick={onClick}
      className="bg-white rounded-3xl overflow-hidden shadow-sm border border-[#EBE8E0] cursor-pointer group"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        {memorial.photo ? (
          <img 
            src={memorial.photo} 
            alt={memorial.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
            <span className="text-6xl">🐾</span>
          </div>
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Anniversary Badge */}
        {isAnniversary && (
          <div className="absolute top-3 right-3 bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <Star className="w-3 h-3" />
            Anniversary Today
          </div>
        )}
        
        {/* Privacy Badge */}
        {memorial.privacy === 'private' && (
          <div className="absolute top-3 left-3 bg-black/50 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
            <Lock className="w-3 h-3" />
          </div>
        )}
        
        {/* Name */}
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="text-white text-xl font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            {memorial.name}
          </h3>
          <p className="text-white/80 text-sm">{memorial.breed}</p>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <p className="text-sm text-[#7A7571] mb-3 line-clamp-2">
          {memorial.tribute || 'Forever in our hearts...'}
        </p>
        
        <div className="flex items-center justify-between text-xs text-[#7A7571]">
          <span>
            {new Date(memorial.birth_date).getFullYear()} - {new Date(memorial.passed_date).getFullYear()}
          </span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              🕯️ {memorial.candles || 0}
            </span>
            <span className="flex items-center gap-1">
              💐 {memorial.flowers || 0}
            </span>
            <span className="flex items-center gap-1">
              💬 {memorial.messages?.length || 0}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Create Memorial Modal
const CreateMemorialModal = ({ isOpen, onClose, onSave }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    breed: '',
    birth_date: '',
    passed_date: '',
    photo: '',
    tribute: '',
    favorite_memory: '',
    privacy: 'public',
    allow_donations: true
  });
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // Get Cloudinary signature
      const sigRes = await fetch(`${API_URL}/api/puppoto/cloudinary/signature?folder=puppoto/memorials`, {
        credentials: 'include'
      });
      
      if (sigRes.ok) {
        const sig = await sigRes.json();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', sig.api_key);
        formData.append('timestamp', sig.timestamp);
        formData.append('signature', sig.signature);
        formData.append('folder', sig.folder);

        const uploadRes = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
          { method: 'POST', body: formData }
        );

        if (uploadRes.ok) {
          const data = await uploadRes.json();
          setForm(p => ({ ...p, photo: data.secure_url }));
          toast.success('Photo uploaded');
        }
      } else {
        // Fallback to local preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setForm(p => ({ ...p, photo: reader.result }));
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!form.name || !form.passed_date) {
      toast.error('Please fill in the required fields');
      return;
    }
    onSave(form);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Create Memorial
            </h2>
            <p className="text-sm text-[#7A7571]">Honor your beloved companion</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <div 
              key={s}
              className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-purple-500' : 'bg-[#EBE8E0]'}`}
            />
          ))}
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Photo Upload */}
            <div className="text-center">
              <div 
                className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center cursor-pointer border-4 border-white shadow-lg"
                onClick={() => document.getElementById('memorial-photo').click()}
              >
                {form.photo ? (
                  <img src={form.photo} alt="Memorial" className="w-full h-full object-cover" />
                ) : uploading ? (
                  <div className="animate-pulse">📷</div>
                ) : (
                  <Camera className="w-8 h-8 text-purple-400" />
                )}
              </div>
              <input
                id="memorial-photo"
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <p className="text-sm text-[#7A7571] mt-2">Add a photo</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">Name *</label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Your pet's name"
                className="rounded-xl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">Breed</label>
              <Input
                value={form.breed}
                onChange={e => setForm(p => ({ ...p, breed: e.target.value }))}
                placeholder="e.g., Golden Retriever, Tabby Cat..."
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1A1817] mb-1">Born</label>
                <Input
                  type="date"
                  value={form.birth_date}
                  onChange={e => setForm(p => ({ ...p, birth_date: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1817] mb-1">Passed *</label>
                <Input
                  type="date"
                  value={form.passed_date}
                  onChange={e => setForm(p => ({ ...p, passed_date: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
            </div>

            <Button
              onClick={() => setStep(2)}
              className="w-full h-12 bg-purple-500 hover:bg-purple-600 text-white rounded-xl"
            >
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: Tribute */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">Tribute Message</label>
              <textarea
                value={form.tribute}
                onChange={e => setForm(p => ({ ...p, tribute: e.target.value }))}
                placeholder="Share what made them special..."
                className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-32"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">Favorite Memory</label>
              <textarea
                value={form.favorite_memory}
                onChange={e => setForm(p => ({ ...p, favorite_memory: e.target.value }))}
                placeholder="Tell us about a special moment..."
                className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-24"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
              >
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                className="flex-1 h-12 bg-purple-500 hover:bg-purple-600 text-white rounded-xl"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Settings */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-3">Privacy</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setForm(p => ({ ...p, privacy: 'public' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    form.privacy === 'public' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-[#EBE8E0] hover:border-purple-200'
                  }`}
                >
                  <Globe className={`w-6 h-6 mx-auto mb-2 ${form.privacy === 'public' ? 'text-purple-500' : 'text-[#7A7571]'}`} />
                  <span className="block font-medium text-sm">Public</span>
                  <span className="block text-xs text-[#7A7571]">Community can see & support</span>
                </button>
                <button
                  onClick={() => setForm(p => ({ ...p, privacy: 'private' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    form.privacy === 'private' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-[#EBE8E0] hover:border-purple-200'
                  }`}
                >
                  <Lock className={`w-6 h-6 mx-auto mb-2 ${form.privacy === 'private' ? 'text-purple-500' : 'text-[#7A7571]'}`} />
                  <span className="block font-medium text-sm">Private</span>
                  <span className="block text-xs text-[#7A7571]">Only you & shared links</span>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-[#F5F3EE] rounded-xl">
              <div>
                <span className="font-medium text-[#1A1817]">Allow Donations</span>
                <p className="text-sm text-[#7A7571]">Let visitors donate to pet charities in their name</p>
              </div>
              <button
                onClick={() => setForm(p => ({ ...p, allow_donations: !p.allow_donations }))}
                className={`w-12 h-7 rounded-full transition-colors ${
                  form.allow_donations ? 'bg-purple-500' : 'bg-[#EBE8E0]'
                }`}
              >
                <motion.div 
                  className="w-5 h-5 bg-white rounded-full shadow"
                  animate={{ x: form.allow_donations ? 24 : 4 }}
                />
              </button>
            </div>

            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <span className="text-4xl mb-2 block">🌈</span>
              <p className="text-purple-700 font-medium">
                {form.name || 'Your companion'} will always be remembered
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setStep(2)}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 h-12 bg-purple-500 hover:bg-purple-600 text-white rounded-xl"
              >
                Create Memorial
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Send Gift Modal
const SendGiftModal = ({ isOpen, onClose, onSend, petName }) => {
  const [selectedGift, setSelectedGift] = useState(null);
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
          Send Love to {petName}
        </h3>

        <div className="grid grid-cols-3 gap-3 mb-4">
          {VIRTUAL_GIFTS.map(gift => (
            <button
              key={gift.id}
              onClick={() => setSelectedGift(gift)}
              className={`p-4 rounded-xl border-2 transition-all ${
                selectedGift?.id === gift.id 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-[#EBE8E0] hover:border-purple-200'
              }`}
            >
              <span className="text-3xl block mb-1">{gift.emoji}</span>
              <span className="text-xs text-[#7A7571]">{gift.name}</span>
            </button>
          ))}
        </div>

        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Add a message (optional)..."
          className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-20 mb-4"
        />

        <Button
          onClick={() => { onSend(selectedGift, message); setSelectedGift(null); setMessage(''); }}
          disabled={!selectedGift}
          className="w-full h-12 bg-purple-500 hover:bg-purple-600 text-white rounded-xl"
        >
          <Send className="w-4 h-4 mr-2" />
          Send {selectedGift?.name || 'Gift'}
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Main Rainbow Bridge Component
const RainbowBridge = () => {
  const navigate = useNavigate();
  const [memorials, setMemorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMemorial, setSelectedMemorial] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);

  useEffect(() => {
    fetchMemorials();
  }, []);

  const fetchMemorials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/puppoto/memorials`);
      if (response.ok) {
        const data = await response.json();
        setMemorials(data.memorials || []);
      }
    } catch (error) {
      console.error('Failed to fetch memorials:', error);
      // Demo data
      setMemorials([
        {
          id: '1',
          name: 'Buddy',
          breed: 'Golden Retriever',
          photo: 'https://images.unsplash.com/photo-1552053831-71594a27632d?w=400',
          birth_date: '2010-03-15',
          passed_date: '2023-08-20',
          tribute: 'The most loyal friend anyone could ask for. He brought sunshine into our lives every single day.',
          favorite_memory: 'Running on the beach together every Sunday morning.',
          privacy: 'public',
          candles: 47,
          flowers: 23,
          messages: [
            { author: 'Sarah', text: 'Such a beautiful soul. Rest easy, Buddy. 🌈', date: '2024-01-15' }
          ]
        },
        {
          id: '2',
          name: 'Luna',
          breed: 'Border Collie',
          photo: 'https://images.unsplash.com/photo-1503256207526-0d5d80fa2f47?w=400',
          birth_date: '2015-06-01',
          passed_date: '2024-02-14',
          tribute: 'The smartest, most gentle girl. She knew exactly when we needed comfort.',
          favorite_memory: 'Teaching her new tricks - she learned them all in minutes!',
          privacy: 'public',
          candles: 31,
          flowers: 18,
          messages: []
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMemorial = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/api/puppoto/memorials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Memorial created. Rest in peace, ' + formData.name + ' 🌈');
        fetchMemorials();
      } else {
        // Add locally for demo
        setMemorials(prev => [{
          ...formData,
          id: Date.now().toString(),
          candles: 0,
          flowers: 0,
          messages: []
        }, ...prev]);
        toast.success('Memorial created. Rest in peace, ' + formData.name + ' 🌈');
      }
    } catch (error) {
      // Add locally for demo
      setMemorials(prev => [{
        ...formData,
        id: Date.now().toString(),
        candles: 0,
        flowers: 0,
        messages: []
      }, ...prev]);
      toast.success('Memorial created. Rest in peace, ' + formData.name + ' 🌈');
    }
    setShowCreateModal(false);
  };

  const handleSendGift = (gift, message) => {
    if (!selectedMemorial || !gift) return;

    setMemorials(prev => prev.map(m => {
      if (m.id === selectedMemorial.id) {
        return {
          ...m,
          candles: gift.id === 'candle' ? (m.candles || 0) + 1 : m.candles,
          flowers: gift.id === 'flower' ? (m.flowers || 0) + 1 : m.flowers,
          messages: message ? [...(m.messages || []), {
            author: 'Anonymous',
            text: message,
            gift: gift.emoji,
            date: new Date().toISOString()
          }] : m.messages
        };
      }
      return m;
    }));

    toast.success(`${gift.emoji} sent to ${selectedMemorial.name}`);
    setShowGiftModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-blue-50">
      {/* Ambient floating elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <FloatingCandle key={i} delay={i * 0.8} />
        ))}
      </div>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBE8E0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/puppoto')}
            className="flex items-center gap-2 text-[#1A1817] hover:text-purple-600 transition"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">Back to Puppoto</span>
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-2xl">🌈</span>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Rainbow Bridge
            </span>
          </div>
          
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-500 hover:bg-purple-600 text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Memorial
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 text-center relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          <span className="text-6xl mb-6 block">🌈</span>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            The Rainbow Bridge
          </h1>
          
          <p className="text-lg text-[#7A7571] mb-4 leading-relaxed">
            A place to honor and remember our beloved companions who have crossed over. 
            They may be gone, but they are never forgotten.
          </p>

          <p className="text-purple-600 italic">
            "Until we meet again at the Rainbow Bridge..."
          </p>
        </motion.div>
      </section>

      {/* Memorial Grid */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-12 text-[#7A7571]">Loading memorials...</div>
          ) : memorials.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">🕯️</span>
              <h3 className="text-xl font-bold text-[#1A1817] mb-2">No memorials yet</h3>
              <p className="text-[#7A7571] mb-4">Be the first to honor a beloved companion</p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-purple-500 hover:bg-purple-600 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Memorial
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {memorials.filter(m => m.privacy === 'public').map(memorial => (
                <MemorialCard
                  key={memorial.id}
                  memorial={memorial}
                  onClick={() => { setSelectedMemorial(memorial); setShowGiftModal(true); }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Donation Section */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Honor Their Memory
          </h2>
          <p className="text-[#7A7571] mb-8">
            Make a donation in your pet's name to help other animals in need
          </p>
          
          <div className="grid md:grid-cols-3 gap-4">
            {DONATION_PARTNERS.map(partner => (
              <a
                key={partner.id}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 border border-[#EBE8E0] hover:border-purple-300 hover:shadow-md transition-all group"
              >
                <Heart className="w-8 h-8 text-purple-400 mx-auto mb-3 group-hover:text-purple-600 transition" />
                <h3 className="font-semibold text-[#1A1817] mb-1">{partner.name}</h3>
                <span className="text-sm text-purple-500 flex items-center justify-center gap-1">
                  Donate <ExternalLink className="w-3 h-3" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#EBE8E0] bg-white/50">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm text-[#7A7571]">
            🌈 The Rainbow Bridge • A Puppoto Memorial Space • Made with love
          </p>
        </div>
      </footer>

      {/* Modals */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateMemorialModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreateMemorial}
          />
        )}
        {showGiftModal && selectedMemorial && (
          <SendGiftModal
            isOpen={showGiftModal}
            onClose={() => { setShowGiftModal(false); setSelectedMemorial(null); }}
            onSend={handleSendGift}
            petName={selectedMemorial.name}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default RainbowBridge;
